/*!
 * Caco — Sale Collection Filter (single-pass, no MutationObserver loop)
 * On /collections/sale:
 *   1. For each product card, check if its themed price has `.wd40-price--sale`.
 *      If yes → mark `caco-sale-ok` and show as-is.
 *   2. If not → fetch /products/{handle}.js, find the variant with the biggest
 *      discount. If one exists, rewrite the card's price + add struck compare
 *      + inject a "SAVE UP TO X%" badge, then mark `caco-sale-ok`.
 *   3. Cards left without `caco-sale-ok` (and not promo cards) get
 *      `.caco-sale-hide` → display:none via CSS.
 */
(function () {
  if (!/\/collections\/sale\b/.test(location.pathname)) return;

  var CARD_SEL = '.wd40-card';
  var productCache = new Map();
  var ran = false;

  function fmtMoney(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function handleFromCard(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = link.getAttribute('href').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  function findBestSaleVariant(product) {
    // /products/{handle}.js returns prices in CENTS as integers (e.g. 360 = $3.60).
    var best = null;
    (product.variants || []).forEach(function (v) {
      var price = Number(v.price) || 0;
      var cap = Number(v.compare_at_price) || 0;
      if (cap > 0 && price > 0 && cap > price) {
        var delta = cap - price;
        if (!best || delta > best.delta) {
          best = {
            id: v.id,
            price: price,
            compare: cap,
            delta: delta,
            percent: Math.round((delta / cap) * 100),
            featured_image: v.featured_image || null
          };
        }
      }
    });
    return best;
  }

  function enrichCard(card, sale) {
    var priceEl = card.querySelector('.wd40-price');
    if (!priceEl) return;
    priceEl.classList.add('wd40-price--sale');

    var current = priceEl.querySelector('.wd40-price-current');
    if (current) current.textContent = fmtMoney(sale.price);

    var compare = priceEl.querySelector('.wd40-price-compare');
    if (!compare) {
      compare = document.createElement('span');
      compare.className = 'wd40-price-compare';
      priceEl.appendChild(compare);
    }
    compare.textContent = fmtMoney(sale.compare);
    compare.style.display = '';

    // Append ?variant=ID to every product link on the card so the user lands
    // on the discounted variant by default.
    if (sale.id) {
      card.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
        try {
          var url = new URL(a.getAttribute('href'), location.origin);
          url.searchParams.set('variant', sale.id);
          a.setAttribute('href', url.pathname + url.search);
        } catch (e) { /* ignore malformed hrefs */ }
      });
    }

    // Swap the card image to the variant's featured_image if present.
    if (sale.featured_image && sale.featured_image.src) {
      var newSrc = sale.featured_image.src;
      // Strip protocol for relative-safe loading
      if (newSrc.indexOf('//') === 0) newSrc = location.protocol + newSrc;
      card.querySelectorAll('img.js-product-image-main, img.wd40-card-image, .wd40-card-image-hover img').forEach(function (img) {
        // Use a reasonable size param if Shopify CDN
        var swappedSrc = newSrc;
        try {
          var u = new URL(newSrc, location.origin);
          if (/cdn\.shopify\.com|shop_images|cdn\/shop/.test(u.hostname + u.pathname)) {
            u.searchParams.set('width', '600');
            swappedSrc = u.toString();
          }
        } catch (e) {}
        img.removeAttribute('srcset');
        img.removeAttribute('data-srcset');
        img.src = swappedSrc;
        img.setAttribute('data-src', swappedSrc);
      });
    }

    if (!card.querySelector('.wd40-sale-badge')) {
      // Reuse the theme's native badge structure so styling matches existing
      // "Save up to X%" badges (gradient pill, position, font, shadow).
      var badge = document.createElement('span');
      badge.className = 'wd40-badge wd40-sale-badge caco-sale-badge';
      badge.setAttribute('role', 'text');
      var text = document.createElement('span');
      text.className = 'wd40-badge-text';
      text.textContent = 'Save up to ' + sale.percent + '%';
      badge.appendChild(text);

      // Find or create the badges wrapper (.wd40-badges)
      var wrapper = card.querySelector('.wd40-badges');
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'wd40-badges';
        // Ensure card can host an absolutely-positioned wrapper
        if (getComputedStyle(card).position === 'static') card.style.position = 'relative';
        // Insert as the first child so it sits on top
        card.insertBefore(wrapper, card.firstChild);
      }
      wrapper.appendChild(badge);
    }
  }

  function markOk(card) {
    card.classList.add('caco-sale-ok');
  }

  function markHide(card) {
    card.classList.add('caco-sale-hide');
    var wrap = card.parentElement;
    if (wrap && wrap.parentElement && wrap.parentElement.classList.contains('wd40-product-grid-items')) {
      wrap.classList.add('caco-sale-hide');
    }
  }

  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    var p = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    productCache.set(handle, p);
    return p;
  }

  function finalize(visibleCount) {
    document.body.classList.remove('caco-sale-loading');
    document.body.classList.add('caco-sale-ready');
    var grid = document.querySelector('.wd40-product-grid-items, .wd40-product-grid');
    if (grid && visibleCount === 0 && !document.getElementById('caco-sale-empty')) {
      var empty = document.createElement('div');
      empty.id = 'caco-sale-empty';
      empty.style.cssText = 'grid-column:1/-1;padding:64px 24px;text-align:center;color:#5b6b80;';
      empty.innerHTML =
        '<div style="font-size:.72rem;letter-spacing:.16em;text-transform:uppercase;color:#1b6fb0;font-weight:700;margin-bottom:12px;">No active discounts</div>' +
        '<p style="font-size:1rem;max-width:42ch;margin:0 auto;line-height:1.55;">There are no products on sale right now. Check back soon.</p>';
      grid.appendChild(empty);
    }
  }

  function run() {
    if (ran) return;
    ran = true;
    var cards = Array.from(document.querySelectorAll(CARD_SEL));
    var visible = 0;
    var pending = [];

    cards.forEach(function (card) {
      if (card.classList.contains('wd40-promo-card')) {
        markOk(card);
        visible++;
        return;
      }
      var priceEl = card.querySelector('.wd40-price');
      if (!priceEl) {
        markHide(card);
        return;
      }
      if (priceEl.classList.contains('wd40-price--sale')) {
        markOk(card);
        visible++;
        return;
      }
      var handle = handleFromCard(card);
      if (!handle) {
        markHide(card);
        return;
      }
      pending.push(
        fetchProduct(handle).then(function (product) {
          if (!product) {
            markHide(card);
            return;
          }
          var sale = findBestSaleVariant(product);
          if (sale) {
            enrichCard(card, sale);
            markOk(card);
            visible++;
          } else {
            markHide(card);
          }
        }).catch(function () { markHide(card); })
      );
    });

    if (pending.length) {
      Promise.all(pending).then(function () { finalize(visible); });
    } else {
      finalize(visible);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
